// @ts-check
/** @typedef {import('../types/api').Order} Order */
/** @typedef {import('../types/api').PaginatedOrders} PaginatedOrders */
/** @typedef {import('../types/api').OrderStats} OrderStats */
/** @typedef {import('../types/api').OrderTransaction} OrderTransaction */

import api from '../utils/apiClient';
import { assertArray, assertShape } from '../utils/apiGuards';

const adminOrderService = {
  /**
   * @param {Object} [params]
   * @returns {Promise<PaginatedOrders>}
   */
  async getAllOrders(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.order_status) queryParams.append('order_status', params.order_status);
    if (params.payment_status) queryParams.append('payment_status', params.payment_status);
    if (params.order_type) queryParams.append('order_type', params.order_type);
    if (params.sales_channel) queryParams.append('sales_channel', params.sales_channel);

    const data = await api.get(`/api/admin/orders/?${queryParams}`);
    assertShape(data, ['items', 'total'], 'adminOrderService.getAllOrders');
    return data;
  },

  /**
   * @returns {Promise<OrderStats>}
   */
  async getOrderStats() {
    const data = await api.get('/api/admin/orders/stats');
    assertShape(data, ['total_orders'], 'adminOrderService.getOrderStats');
    return data;
  },

  /**
   * @param {string} orderId
   * @returns {Promise<Order>}
   */
  async getOrder(orderId) {
    const data = await api.get(`/api/admin/orders/${orderId}`);
    assertShape(data, ['id'], 'adminOrderService.getOrder');
    return data;
  },

  /**
   * Set or fix the promised dispatch date and each piece's customer measurements and fit note.
   * Only what is sent changes; null clears.
   * @param {string} orderId
   * @param {{promised_dispatch_date?: string | null, items?: Array<{item_id: string, measurements?: Record<string, number> | null, fit_notes?: string | null}>}} data
   */
  async updateProductionDetails(orderId, data) {
    return api.patch(`/api/admin/orders/${orderId}/production-details`, data);
  },

  /**
   * The order's GST tax invoice, if one has been generated.
   * @param {string} orderId
   * @returns {Promise<Object>}
   */
  async getInvoice(orderId) {
    return api.get(`/api/admin/orders/${orderId}/invoice`);
  },

  /**
   * Generate (or re-fetch, if one already exists) this order's invoice by hand - for when
   * auto-generation didn't run, e.g. a product's tax rate was missing and has since been fixed.
   * @param {string} orderId
   * @returns {Promise<Object>}
   */
  async generateInvoice(orderId) {
    return api.post(`/api/admin/orders/${orderId}/invoice`);
  },

  /**
   * Re-render the invoice PDF from the order's current numbers - for when the invoice row was
   * corrected after the file was first generated, so the download matches the DB again.
   * @param {string} orderId
   * @returns {Promise<Object>}
   */
  async regenerateInvoice(orderId) {
    return api.post(`/api/admin/orders/${orderId}/invoice/regenerate`);
  },

  /**
   * @param {string} orderId
   * @param {Object} statusData
   * @returns {Promise<Order>}
   */
  async updateOrderStatus(orderId, statusData) {
    return api.patch(`/api/admin/orders/${orderId}/status`, statusData);
  },

  /**
   * @param {string} orderId
   * @returns {Promise<OrderTransaction[]>}
   */
  async getOrderTransactions(orderId) {
    const data = await api.get(`/api/admin/orders/${orderId}/transactions`);
    assertArray(data, 'adminOrderService.getOrderTransactions');
    return data;
  },

  /**
   * @param {string} orderId
   * @returns {Promise<import('../types/api').OrderEvent[]>}
   */
  async getOrderEvents(orderId) {
    const data = await api.get(`/api/admin/orders/${orderId}/events`);
    assertArray(data, 'adminOrderService.getOrderEvents');
    return data;
  },

  /**
   * Permanently removes an order - only allowed when it has no GST invoice and no real gateway
   * payment (the backend rejects anything else with a clear reason; use Cancel for those instead).
   * @param {string} orderId
   * @returns {Promise<{message: string}>}
   */
  async deleteOrder(orderId) {
    return api.del(`/api/admin/orders/${orderId}`);
  },

  /**
   * Customer/shipping fields, editable anytime except shipping_state once an invoice already
   * exists. Only fields you pass change - omit the rest.
   * @param {string} orderId
   * @param {Object} data
   * @returns {Promise<{message: string, order: Order}>}
   */
  async updateCustomerDetails(orderId, data) {
    return api.patch(`/api/admin/orders/${orderId}/customer-details`, data);
  },

  /**
   * City options and GST state for an Indian PIN code (India Post), to prefill a delivery address.
   * @param {string} pincode
   * @returns {Promise<{state: string, cities: string[]}>}
   */
  async lookupPincode(pincode) {
    return api.get(`/api/admin/pincode-lookup/${pincode}`);
  },

  /**
   * Adds a new piece to the order (only before it has an invoice) - deducts real stock.
   * @param {string} orderId
   * @param {{product_id: string, variant_id: string, quantity?: number, unit_price?: number}} data
   * @returns {Promise<{message: string, order: Order}>}
   */
  async addOrderItem(orderId, data) {
    return api.post(`/api/admin/orders/${orderId}/items`, data);
  },

  /**
   * Changes a piece's quantity, price, and/or size/color (only before it has an invoice) -
   * stock is adjusted to match, in whichever direction the change goes.
   * @param {string} orderId
   * @param {string} itemId
   * @param {{quantity?: number, unit_price?: number, variant_id?: string}} data
   * @returns {Promise<{message: string, order: Order}>}
   */
  async updateOrderItem(orderId, itemId, data) {
    return api.patch(`/api/admin/orders/${orderId}/items/${itemId}`, data);
  },

  /**
   * Removes a piece from the order (only before it has an invoice, and never down to zero
   * pieces) - restores its stock.
   * @param {string} orderId
   * @param {string} itemId
   * @returns {Promise<{message: string, order: Order}>}
   */
  async removeOrderItem(orderId, itemId) {
    return api.del(`/api/admin/orders/${orderId}/items/${itemId}`);
  },

  /**
   * Record money received against an order after it was created - the balance of a
   * partial/advance payment, or a correction.
   * @param {string} orderId
   * @param {{amount: number, method: string, reference?: string | null, note?: string | null, allow_overpayment?: boolean}} data
   * @returns {Promise<{message: string, payment_status: string, amount_paid: number, balance_due: number}>}
   */
  async recordPayment(orderId, data) {
    return api.post(`/api/admin/orders/${orderId}/payments`, data);
  },

  /**
   * Agree that the courier collects the remaining balance at delivery (COD). Before the order ships.
   * @param {string} orderId
   * @param {boolean} enabled
   * @returns {Promise<{balance_on_delivery: boolean}>}
   */
  async setBalanceOnDelivery(orderId, enabled) {
    return api.patch(`/api/admin/orders/${orderId}/balance-on-delivery`, { enabled });
  },

  /**
   * Every payment recorded against this order (the ledger behind amount_paid/balance_due).
   * @param {string} orderId
   * @returns {Promise<import('../types/api').OrderPayment[]>}
   */
  async getOrderPayments(orderId) {
    const data = await api.get(`/api/admin/orders/${orderId}/payments`);
    assertArray(data, 'adminOrderService.getOrderPayments');
    return data;
  },

  /**
   * Email whatever message matches this order's CURRENT payment_status - never a free choice; to
   * send a different message, change the real payment status first (Record a Payment). Requires
   * a real email on the order - use the WhatsApp option instead for a POS sale with none on file.
   * @param {string} orderId
   * @returns {Promise<{message: string, recipient: string}>}
   */
  async sendPaymentStatusEmail(orderId) {
    return api.post(`/api/admin/orders/${orderId}/send-payment-status-email`, {});
  },

  /**
   * Email whatever message matches this order's CURRENT order_status - never a free choice; to
   * send a different message, change the real order status first (Update Order Status).
   * @param {string} orderId
   * @returns {Promise<{message: string, recipient: string}>}
   */
  async sendFulfillmentStatusEmail(orderId) {
    return api.post(`/api/admin/orders/${orderId}/send-fulfillment-status-email`, {});
  },
};

export default adminOrderService;
